import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';

import { Ati } from '@atlassian/ari';

import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { fg } from '@confluence/feature-gating';
import {
	checkShouldShowLozenge,
	setLozengeShownTrackerMax,
} from '@confluence/live-pages-utils/entry-points/liveDocsLozengeUtils.ts';

import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

import { buildCreationLink } from '../../CreateContentRouteComponent/index.ts';
import { useHybridLinkClick } from '../../CreateContentPopover/CreateContentPopoverItems/hybridLinkHelper.ts';
import { ConfluencePageSubType } from '../../queries/__types__/CreateContentMutation.ts';
import { ItemTitleWithLozenge } from '../ItemTitle.tsx';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.liveDoc.title',
		defaultMessage: 'Live Doc',
		description: 'title indicating that this option will create a live doc',
	},
	newLozenge: {
		id: 'create-blank-fabric-page.universal-create.liveDoc.new.lozenge',
		defaultMessage: 'New',
		description: 'Text for the new lozenge. Informs users that the associated feature is new.',
	},
});

const CREATE_LIVE_DOC_POPOVER_ITEM = 'create-live-doc-popover-item';

export function useLiveDocItem({
	parentId,
	source,
	spaceKey,
	onClick,
	hideLozenge,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();
	const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
		source: CREATE_LIVE_DOC_POPOVER_ITEM,
	});

	const createHref = buildCreationLink({
		contentType: 'page',
		subType: ConfluencePageSubType.LIVE,
		parentPageId: parentId,
		withFallback: true,
		source,
		spaceKey,
	});

	const hybridLinkClick = useHybridLinkClick(createHref);
	const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
		onClick('createLiveDocPopoverItem');
		hybridLinkClick(e);
		setLozengeShownTrackerMax(); // Disable live docs lozenge after user creates live doc
	};

	// shouldShowLozenge should be stabilized so it is not checked each re-render
	// we also do not want the lozenge to disappear if it's already shown, even if tracker value changes
	const shouldShowLozenge = useMemo(() => checkShouldShowLozenge(), []);
	// Lozenge is always removed only on Hello.
	const titleItem =
		fg('confluence_remove_live_page_lozenge_hello') || !shouldShowLozenge || hideLozenge ? (
			formatMessage(messages.title)
		) : (
			<ItemTitleWithLozenge lozenge={formatMessage(messages.newLozenge)}>
				{formatMessage(messages.title)}
			</ItemTitleWithLozenge>
		);

	return {
		ati: Ati.parse('ati:cloud:confluence:livedoc'),
		children: titleItem,
		onClick: enforceStorageLimit(handleClick),
		elemBefore: PageLiveDocIcon,
		...(!shouldBlockCreate && { href: createHref }),
		testId: `create-menu-${shouldBlockCreate ? 'button' : 'link'}-item`,
	};
}
